import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from '../../design-system/base/TrueImpactText';
import TrueImpactRadio from '../../design-system/components/TrueImpactRadio';
import TrueImpactSearchBox from '../../design-system/components/TrueImpactSearchBox';
import { SPACERS } from '../../design-system/settings/variables/Spacers';
import { ProgramManager, getFullName } from './utils';
import { MEDIA_BREAKPOINTS } from '../../design-system/settings/variables/Breakpoints';

interface ProgramManagerModalContentProps {
  errorText?: string;
  currentProgramManager: ProgramManager | undefined;
  manager: ProgramManager | undefined;
  options: ProgramManager[];
  handleManagerChange: (manager: ProgramManager) => unknown;
}

const StyledDiv = styled.div`
  margin: auto;
  width: 741px;
  overflow: auto;
  max-height: 80vh;
  padding: ${SPACERS.large};
  .modal-title {
    margin-bottom: ${SPACERS.medium};
  }
  ${MEDIA_BREAKPOINTS.smallerThanMedium} {
    width: 100%;
  }
`;

const ProgramManagerModalContent: React.FC<ProgramManagerModalContentProps> = ({
  options,
  manager,
  errorText,
  currentProgramManager,
  handleManagerChange,
}) => {
  const [search, setSearch] = useState<string>('');

  const filteredOptions = useMemo(
    () =>
      options.filter(o =>
        getFullName(o.firstName, o.lastName)
          .toLowerCase()
          .includes(search.toLowerCase()),
      ),
    [search, options],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
    },
    [setSearch],
  );

  const handleClear = useCallback(() => {
    setSearch('');
  }, [setSearch]);

  return (
    <StyledDiv>
      <TrueImpactText variant="heading" className="modal-title">
        Select program manager
      </TrueImpactText>
      <TrueImpactSearchBox
        placeholder="Search name"
        value={search}
        onChange={handleChange}
        autoFocus={true}
        onClear={handleClear}
      />
      <TrueImpactRadio
        nullText={
          <TrueImpactText variant="body-bold" align="center">
            No managers were found.
          </TrueImpactText>
        }
        selectedOption={manager}
        options={filteredOptions}
        compare={(o, i) => o.userId === currentProgramManager?.userId}
        getId={o => o.userId}
        getLabel={o => {
          const fullName = getFullName(o.firstName, o.lastName);
          return o.userId !== currentProgramManager?.userId
            ? fullName
            : `${fullName} (Current Manager)`;
        }}
        onChange={handleManagerChange}
      />
      {errorText && (
        <TrueImpactText color="critical">{errorText}</TrueImpactText>
      )}
    </StyledDiv>
  );
};

export default ProgramManagerModalContent;
